'use es6';

import PropTypes from 'prop-types';
import { Component } from 'react';
import { List } from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Select } from 'foundations-components/transitional/components/Select';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import { sortTeamsAlphabetically } from '../lib/teams';
import Team from '../records/Team';
import I18nUnescapedText from 'I18n/utils/unescapedText';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import memoizeOne from 'react-utils/memoizeOne';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const coerceTeamIdtoString = teamId => {
  return typeof teamId === 'number' ? teamId.toString() : teamId;
};
const teamIdsAsStrings = teamIds => teamIds.map(coerceTeamIdtoString);
class TeamSelect extends Component {
  constructor(props) {
    super(props);
    this.getOptions = memoizeOne((teams, customOptions) => {
      const teamOptions = this.getTeamOptions(sortTeamsAlphabetically(teams.map(Team.fromReference)));
      return customOptions.map(option => ({
        text: option.label || option.text,
        value: option.value,
        disabled: !!option.disabled
      })).concat(teamOptions).toJS();
    });
    this.handleChange = this.handleChange.bind(this);
    this.getTeamOptions = this.getTeamOptions.bind(this);
    this.renderButtonText = this.renderButtonText.bind(this);
    this.state = {
      filter: ''
    };
  }
  handleChange({
    target: {
      value
    }
  }) {
    this.props.onChange(SyntheticEvent(this.props.multi ? List(value) : value));
  }
  getTeamOptions(teams) {
    const {
      transformOptions
    } = this.props;
    return teams.map(team => {
      let teamOption = {
        text: team.name,
        value: String(team.id)
      };
      const shouldApplyTransformOptions = transformOptions && typeof transformOptions === 'function';
      if (shouldApplyTransformOptions) {
        teamOption = transformOptions(teamOption);
      }
      if (team.childTeams.size) {
        const selectAllOption = {
          text: I18nUnescapedText('uiAddonTeams.selectAll', {
            teamName: team.name
          }),
          selectAll: true,
          options: List.of(teamOption).concat(this.getTeamOptions(team.childTeams)).toJS()
        };
        return shouldApplyTransformOptions ? transformOptions(selectAllOption) : selectAllOption;
      } else {
        return teamOption;
      }
    });
  }
  getButtonContent() {
    const {
      ButtonContent,
      showTags
    } = this.props;
    if (ButtonContent && ButtonContent()) {
      return ButtonContent;
    } else if (!showTags) {
      return this.renderButtonText;
    }
    return null;
  }
  renderButtonText() {
    const {
      value
    } = this.props;
    if (value.size === 0) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "uiAddonTeams.selectTeams",
        options: {
          count: value.size
        }
      });
    } else if (value.size === 1) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "uiAddonTeams.teamSelectLabelOne",
        options: {
          count: value.size
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "uiAddonTeams.teamSelectLabelMany",
      options: {
        count: value.size
      }
    });
  }
  renderPlaceholder() {
    const {
      showPlaceholder,
      placeholder
    } = this.props;
    if (!showPlaceholder) {
      return null;
    }
    return placeholder || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "uiAddonTeams.selectTeams"
    });
  }
  render() {
    const {
      buttonUse,
      customOptions,
      disabled,
      dropdownFooter,
      isLoading,
      menuWidth,
      multi,
      multiCollapseLimit,
      open,
      showTags,
      teams,
      uiFormControlProps,
      value
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
        children: /*#__PURE__*/_jsx("div", {
          id: "ui-addon-team-select",
          children: /*#__PURE__*/_jsx("label", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "uiAddonTeams.selectTeams"
            })
          })
        })
      }), /*#__PURE__*/_jsx(FormControl, Object.assign({
        "aria-labelledby": "ui-addon-team-select"
      }, uiFormControlProps, {
        children: /*#__PURE__*/_jsx(Select, {
          "data-test-id": "ui-addon-team-uiselect",
          anchorType: "button",
          ButtonContent: this.getButtonContent(),
          buttonUse: buttonUse,
          disabled: disabled,
          dropdownFooter: dropdownFooter,
          isLoading: isLoading,
          menuWidth: menuWidth,
          minimumSearchCount: this.props.minimumSearchCount,
          multiCollapseLimit: showTags ? multiCollapseLimit : null,
          multi: multi,
          onChange: this.handleChange,
          open: open,
          options: this.getOptions(teams, customOptions),
          onOpenChange: this.props.onOpenChange,
          placeholder: this.renderPlaceholder(),
          value: multi ? teamIdsAsStrings(value).toJS() : coerceTeamIdtoString(value)
        })
      }))]
    });
  }
}
TeamSelect.defaultProps = {
  buttonUse: 'transparent',
  customOptions: List(),
  isLoading: false,
  menuWidth: 'auto',
  multi: true,
  showPlaceholder: true,
  showTags: false,
  teams: List(),
  uiFormControlProps: {},
  value: List()
};
TeamSelect.propTypes = {
  ButtonContent: PropTypes.func,
  buttonUse: PropTypes.string,
  customOptions: PropTypes.instanceOf(List),
  disabled: PropTypes.bool,
  dropdownFooter: PropTypes.node,
  isLoading: PropTypes.bool,
  menuWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  multiCollapseLimit: PropTypes.number,
  minimumSearchCount: PropTypes.number,
  multi: PropTypes.bool,
  onChange: PropTypes.func,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool,
  placeholder: PropTypes.node,
  showPlaceholder: PropTypes.bool,
  showTags: PropTypes.bool,
  teams: PropTypes.instanceOf(List),
  transformOptions: PropTypes.func,
  uiFormControlProps: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.instanceOf(List), PropTypes.number, PropTypes.string])
};
export default TeamSelect;